import React, { useState } from "react";
import { useMutation } from "@apollo/client";
import { FormattedMessage } from "react-intl";
import { useMissionRole, useMissionRoleInterview, useToast } from "hooks";
import { APPLY_MISSION_ROLE_TEMPLATE } from "graphql/mutations";
import { GET_MISSION_ROLE } from "graphql/queries";

/**
 * useRoleBuilderVetting
 *
 * The draft's vetting modules, which the blueprint step applies and customizes and the review step reads back.
 * They save through their own mutations, so the draft is read back rather than worked off the builder's copy.
 *
 * @param {Object}   draftRole     The draft being built
 * @param {Boolean}  isBlueprint   The blueprint step is showing
 * @param {Boolean}  isReviewStep  The review step is showing
 */
export default function useRoleBuilderVetting({ draftRole, isBlueprint, isReviewStep }) {
  const [appliedTemplateId, setAppliedTemplateId] = useState(draftRole?.builderTemplateId);
  const { toast } = useToast();
  const {
    data: vettingRole,
    loading,
    refetch,
  } = useMissionRole({
    id: draftRole?.slug,
    skip: !isBlueprint && !isReviewStep,
    includeCustomerBudget: true,
  });
  const { data: interview } = useMissionRoleInterview({ missionRoleId: isBlueprint ? draftRole?.id : undefined });
  const [applyBlueprint, { loading: applyingBlueprint }] = useMutation(APPLY_MISSION_ROLE_TEMPLATE);

  const selectBlueprint = (value) => {
    if (!value || value === appliedTemplateId || !draftRole?.id) return;

    setAppliedTemplateId(value);

    return applyBlueprint({
      variables: { input: { id: draftRole.id, templateId: value } },
      refetchQueries: [GET_MISSION_ROLE],
      awaitRefetchQueries: true,
      update: (cache) => {
        cache.evict({ id: "ROOT_QUERY", fieldName: "missionRoleInterview" });
        cache.evict({ id: "ROOT_QUERY", fieldName: "assessment" });
        cache.gc();
      },
      onCompleted: () => toast.success(<FormattedMessage id="useMissionRole.UpdateSuccess" />),
      onError: () => toast.error(<FormattedMessage id="useMissionRole.UpdateError" />),
    });
  };

  return {
    vettingRole,
    vettingLoading: loading || applyingBlueprint,
    refetchVetting: refetch,
    blueprintId: appliedTemplateId,
    applyingBlueprint,
    selectBlueprint,
    hasInterviewQuestions: interview?.questions?.length > 0,
  };
}
